import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../components/ui/input-otp';
import { toast } from 'sonner';
import { Phone, ShieldCheck, Store, Truck, UserCog, AlertCircle, Loader2, MessageSquare, MessageCircle, ArrowLeft } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';
import { isNative } from '../../utils/nativeBridge';
import { loginReturnPath } from '../../utils/loginIntent';
import { loginErrorMessage } from '../../utils/loginErrorMessage';

const RESEND_DELAY = 30;

const LoginPage = ({ riderMode: riderModeProp }) => {
  const navigate = useNavigate();
  const { sendOtp, verifyOtp, storeSettings, registerWithStore } = useAuth();

  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [notRiderAlert, setNotRiderAlert] = useState(false);
  const [otpMethod, setOtpMethod] = useState('sms');
  const [resendTimer, setResendTimer] = useState(0);
  const [agreedToTerms, setAgreedToTerms] = useState(true);

  const isRiderApp = riderModeProp
    || window.location.pathname === '/rider'
    || new URLSearchParams(window.location.search).get('app') === 'rider';
  const [selectedRole, setSelectedRole] = useState(isRiderApp ? 'delivery_boy' : 'customer');

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const doSendOtp = useCallback(async (method) => {
    setLoading(true);
    try {
      await sendOtp(phone, method);
      toast.success(`OTP sent via ${method === 'whatsapp' ? 'WhatsApp' : 'SMS'}`);
      setStep('otp');
      setResendTimer(RESEND_DELAY);
    } catch {
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  }, [phone, sendOtp]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    if (!agreedToTerms) {
      toast.error('Please agree to Terms & Conditions and Privacy Policy');
      return;
    }
    await doSendOtp(otpMethod);
  };

  const handleResend = async (method) => {
    setOtpMethod(method);
    await doSendOtp(method);
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter complete OTP');
      return;
    }

    setLoading(true);
    try {
      const user = await verifyOtp(phone, otp, selectedRole);
      toast.success('Login successful!');

      if (isRiderApp && user.role === 'customer') {
        setNotRiderAlert(true);
        return;
      }

      if (user.role === 'super_admin') {
        navigate('/super-admin');
      } else if (user.role === 'admin' || user.role === 'branch_admin') {
        navigate('/admin');
      } else if (user.role === 'delivery_boy') {
        navigate('/login');
        toast.info('Please use the Delivery App for mobile access');
      } else if (user.role === 'customer') {
        // Auto-register with resolved tenant store (from subdomain, ?tenant= param, or default slug)
        const resolvedAdminId = storeSettings?.admin_id;
        if (resolvedAdminId && (!user.admin_id || user.admin_id !== resolvedAdminId)) {
          try {
            await registerWithStore(resolvedAdminId);
          } catch (e) {
            console.error('Auto-register failed:', e);
          }
          navigate(loginReturnPath() || '/');
        } else if (user.admin_id) {
          // Back to where login was asked for (e.g. Add Address), if anywhere.
          navigate(loginReturnPath() || '/');
        } else {
          navigate('/select-store');
        }
      }
    } catch (error) {
      toast.error(loginErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const allRoles = [
    { id: 'customer', label: 'Customer', icon: Store, description: 'Shop for groceries' },
    { id: 'admin', label: 'Store Admin', icon: UserCog, description: 'Manage your store' },
    { id: 'delivery_boy', label: 'Delivery Partner', icon: Truck, description: 'Deliver orders' },
  ];
  const roles = isRiderApp
    ? allRoles.filter(r => r.id === 'delivery_boy' || r.id === 'admin')
    : allRoles;

  if (notRiderAlert) {
    return (
      <div className="min-h-screen bg-[var(--theme-background)] flex flex-col items-center justify-center p-4" data-testid="not-rider-alert">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-2xl bg-[var(--theme-primary)] flex items-center justify-center mx-auto mb-4">
            <Truck className="w-8 h-8 text-white" />
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-black/5 p-6 space-y-4">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto">
              <AlertCircle className="w-6 h-6 text-amber-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Not Registered as a Rider</h2>
            <p className="text-gray-500 text-sm">
              Your account ({phone}) is not registered as a delivery partner.
              Please contact the store administrator to be added as a rider.
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <Button onClick={() => navigate('/')} className="w-full bg-[var(--theme-primary)] text-white" data-testid="continue-as-customer-btn">
                Continue as Customer
              </Button>
              <Button onClick={() => { setNotRiderAlert(false); setStep('phone'); setOtp(''); }} variant="outline" className="w-full" data-testid="try-different-number-btn">
                Try a Different Number
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--theme-background)] flex flex-col">
      {/* Back Button */}
      <div className="p-3">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/5 transition-colors text-[var(--theme-text-main)]"
          data-testid="login-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="w-20 h-20 rounded-2xl bg-[var(--theme-primary)] flex items-center justify-center mx-auto mb-4">
            <Store className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-[var(--theme-text-main)]">
            Fresh Delivery
          </h1>
          <p className="text-[var(--theme-text-muted)] mt-2">
            Farm fresh groceries at your doorstep
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-[var(--theme-surface)] rounded-2xl p-6 shadow-lg border border-black/5 animate-fade-in">
          {step === 'phone' && (
            <form onSubmit={handleSendOtp} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="font-heading text-xl font-bold text-[var(--theme-text-main)]">
                  Login / Sign Up
                </h2>
                <p className="text-sm text-[var(--theme-text-muted)] mt-1">
                  Enter your phone number to continue
                </p>
              </div>

              {/* Role Selection — hidden on native apps (role is determined by app type) */}
              {!isNative() && (
              <div className="space-y-3">
                <Label className="text-sm font-medium">I am a</Label>
                <div className="grid grid-cols-3 gap-3">
                  {roles.map((role) => (
                    <button
                      key={role.id}
                      type="button"
                      data-testid={`role-${role.id}`}
                      onClick={() => setSelectedRole(role.id)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        selectedRole === role.id
                          ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5'
                          : 'border-black/5 hover:border-[var(--theme-primary)]/50'
                      }`}
                    >
                      <role.icon className={`w-6 h-6 mx-auto mb-1 ${
                        selectedRole === role.id ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-muted)]'
                      }`} />
                      <span className={`text-xs font-medium block ${
                        selectedRole === role.id ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-muted)]'
                      }`}>
                        {role.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Phone Input */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm font-medium">Phone Number</Label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[var(--theme-text-muted)]">
                    <span className="text-sm font-medium">+91</span>
                  </div>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="Enter phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="pl-14 h-12 text-lg input-modern"
                    data-testid="phone-input"
                  />
                  <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--theme-text-muted)]" />
                </div>
              </div>

              {/* OTP Method Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Send OTP via</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    data-testid="otp-method-sms"
                    onClick={() => setOtpMethod('sms')}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      otpMethod === 'sms'
                        ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5'
                        : 'border-black/5 hover:border-[var(--theme-primary)]/50'
                    }`}
                  >
                    <MessageSquare className={`w-4 h-4 ${otpMethod === 'sms' ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-muted)]'}`} />
                    <span className={`text-sm font-medium ${otpMethod === 'sms' ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-muted)]'}`}>SMS</span>
                  </button>
                  <button
                    type="button"
                    data-testid="otp-method-whatsapp"
                    onClick={() => setOtpMethod('whatsapp')}
                    className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                      otpMethod === 'whatsapp'
                        ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5'
                        : 'border-black/5 hover:border-[var(--theme-primary)]/50'
                    }`}
                  >
                    <MessageCircle className={`w-4 h-4 ${otpMethod === 'whatsapp' ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-muted)]'}`} />
                    <span className={`text-sm font-medium ${otpMethod === 'whatsapp' ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-muted)]'}`}>WhatsApp</span>
                  </button>
                </div>
              </div>

              {/* T&C Checkbox */}
              <div className="flex items-start gap-2" data-testid="terms-checkbox-wrapper">
                <Checkbox
                  id="terms-agree"
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                  data-testid="terms-checkbox"
                  className="mt-0.5"
                />
                <span className="text-xs text-[var(--theme-text-muted)] leading-relaxed">
                  I agree to the{' '}
                  <a
                    href="/legal/default/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--theme-primary)] font-medium underline"
                    data-testid="terms-link"
                  >Terms & Conditions</a>{' '}and{' '}
                  <a
                    href="/legal/default/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--theme-primary)] font-medium underline"
                    data-testid="privacy-link"
                  >Privacy Policy</a>
                </span>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-lg font-semibold btn-primary"
                disabled={loading || phone.length < 10 || !agreedToTerms}
                data-testid="send-otp-btn"
              >
                {loading ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Sending...</> : 'Send OTP'}
              </Button>
            </form>
          )}

          {step === 'otp' && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-[var(--theme-primary)]/10 flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="w-8 h-8 text-[var(--theme-primary)]" />
                </div>
                <h2 className="font-heading text-xl font-bold text-[var(--theme-text-main)]">
                  Verify OTP
                </h2>
                <p className="text-sm text-[var(--theme-text-muted)] mt-1">
                  Enter the 6-digit code sent to +91 {phone}
                </p>
              </div>

              {/* OTP Input */}
              <div className="flex justify-center">
                <InputOTP
                  value={otp}
                  onChange={setOtp}
                  maxLength={6}
                  data-testid="otp-input"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                onClick={handleVerifyOtp}
                className="w-full h-12 text-lg font-semibold btn-primary"
                disabled={loading || otp.length !== 6}
                data-testid="verify-otp-btn"
              >
                {loading ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Verifying...</> : 'Verify & Login'}
              </Button>

              {/* Resend OTP section */}
              <div className="text-center space-y-2" data-testid="resend-section">
                {resendTimer > 0 ? (
                  <p className="text-sm text-[var(--theme-text-muted)]" data-testid="resend-timer">
                    Resend OTP in <span className="font-semibold text-[var(--theme-primary)]">{resendTimer}s</span>
                  </p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-[var(--theme-text-muted)]">Didn&apos;t receive the code?</p>
                    <div className="flex items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={() => handleResend('sms')}
                        disabled={loading}
                        className="flex items-center gap-1.5 text-sm font-medium text-[var(--theme-primary)] hover:underline disabled:opacity-50"
                        data-testid="resend-sms-btn"
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        Resend SMS
                      </button>
                      <span className="text-[var(--theme-text-muted)]">|</span>
                      <button
                        type="button"
                        onClick={() => handleResend('whatsapp')}
                        disabled={loading}
                        className="flex items-center gap-1.5 text-sm font-medium text-green-600 hover:underline disabled:opacity-50"
                        data-testid="resend-whatsapp-btn"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        Resend WhatsApp
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => { setStep('phone'); setOtp(''); setResendTimer(0); }}
                className="w-full text-center text-sm text-[var(--theme-primary)] font-medium hover:underline"
                data-testid="change-number-btn"
              >
                Change phone number
              </button>
            </div>
          )}
        </div>

        {/* Browse without login — App Store compliance (customer app only) */}
        {!isRiderApp && (
        <button
          onClick={() => navigate('/')}
          className="w-full text-center mt-4 py-3 text-sm text-[var(--theme-primary)] font-medium hover:underline"
          data-testid="browse-without-login"
        >
          Browse products without login
        </button>
        )}
      </div>
      </div>
    </div>
  );
};

export default LoginPage;
